import React, { createRef, Component } from 'react';
import injectIntl from 'common/translate/injectIntl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { v4 as UUID } from 'uuid';
import bindMultiple from '../../../common/helpers/bindMultiple';
import AbstractControl from '../AbstractControl';
import ResultsCount from '../nested/ResultsCount';
import AutocompleteOptionsList from './nested/AutocompleteOptionsList';
import './css/autocomplete-input.css';
import AutocompleteValue from '../../../common/helpers/AutocompleteValue';
import { getTranslateString } from '../../../common/helpers/getTranslateString';

class AutocompleteControl extends Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.object).isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    value: PropTypes.number,
    style: PropTypes.object,
    focus: PropTypes.bool,
    lockedState: PropTypes.bool,
    disabledState: PropTypes.bool,
    required: PropTypes.bool,
    fullWidth: PropTypes.bool,
    requiredBlue: PropTypes.bool,
    clearInvalidOnBlur: PropTypes.bool,
    isLoading: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    hint: PropTypes.string,
    errorText: PropTypes.string,
    className: PropTypes.string,
    listClassName: PropTypes.string,
    parentClassName: PropTypes.string,
    optionsListTitle: PropTypes.string,
    tooltipText: PropTypes.string,
    placeholder: PropTypes.string,
    optionsToAdd: PropTypes.number,
  };

  static defaultProps = {
    clearInvalidOnBlur: true,
    isLoading: false,
    optionsToAdd: 100, // 10 is minimum
  };

  constructor(props) {
    super(props);
    this.state = {
      optionsList: props.options,
      isType: false,
      isOpen: false,
      optionsCount: props.optionsToAdd,
      inputValue: AutocompleteControl.getOptionLabelFromProps(props, props.value),
    };

    this.abstractComponentRef = null;
    this.inputElemRef = createRef();
    this.inputWrapper = null;

    this.inputAutocompleteValue = AutocompleteValue();
    this.inputNameValue = 'input-' + UUID();

    bindMultiple(
      this,
      this.closeDropDown,
      this.clearFieldValue,
      this.handleOnChange,
      this.handleOnFocus,
      this.handleOnFocusout,
      this.handleInputKeyDown,
      this.handleSelectOption,
      this.fieldOnChange,
      this.openDropDown,
      this.renderMoreOptions,
      this.handleWrapperRefSet
    );
  }

  static getOptionLabelFromProps(props, value) {
    if (value === undefined || value === null || !props.options?.length) {
      return '';
    }

    const activeOption = props.options.find(option => String(option.value) === String(value));

    return activeOption?.text || '';
  }

  static getDerivedStateFromProps(props, state) {
    const nextState = {};

    if (props.options?.length && !state.isType) {
      nextState.optionsList = props.options;
    }

    if (!state.isType) {
      const resolvedLabel = AutocompleteControl.getOptionLabelFromProps(props, props.value);

      if (resolvedLabel !== state.inputValue) {
        nextState.inputValue = resolvedLabel;
      }
    }

    if (props.lockedState && props.id === 'billingDealerIdDealerSelect' && state.isOpen) {
      nextState.isOpen = false;
    }

    return Object.keys(nextState).length ? nextState : null;
  }

  componentDidMount() {
    this.updateInputWrapper();
  }

  componentDidUpdate(prevProps) {
    this.updateInputWrapper();

    if (prevProps.focus && !this.props.focus && this.inputElemRef.current) {
      this.inputElemRef.current.blur();
    }

    if (prevProps.options.length !== this.props.options.length && this.state.inputValue && this.state.isType) {
      const optionsList = this.props.options.filter(o => {
        return o.text.toLowerCase().trim().includes(this.state.inputValue.toLowerCase().trim());
      });

      this.setState({ optionsList, isType: true, isOpen: true });
    }
  }

  updateInputWrapper() {
    this.inputWrapper =
      this.abstractComponentRef?.current?.querySelector('.autocomplete-input-component') || this.inputWrapper;
  }

  render() {
    const {
      // Props for current component
      id,
      value,
      lockedState,
      disabledState,
      className,
      listClassName,
      optionsListTitle,
      fullWidth,
      placeholder,
      intl,
      isLoading,
      // Props for abstract component
      hint,
      style,
      focus,
      label,
      required,
      errorText,
      requiredBlue,
      tooltipText,
      parentClassName,
    } = this.props;

    const componentClassName = classNames('autocomplete-input-component', className);
    const optionsListClassName = classNames('autocomplete-option-list', listClassName);

    const inputAbstractProps = {
      hint,
      style,
      focus,
      label,
      lockedState,
      disabledState,
      errorText,
      required,
      requiredBlue,
      tooltipText,
      parentClassName,
      className: componentClassName,
      labelFor: id,
    };

    const placeholderTextStr =
      typeof placeholder === 'string'
        ? getTranslateString(placeholder, intl)
        : placeholder?.props && intl.formatMessage({ id: placeholder.props.id });

    const listTitle = typeof optionsListTitle === 'string' ? getTranslateString(optionsListTitle, intl) : '';
    const isShowResultCnt = this.state.isOpen && this.state.inputValue.length > 0;
    const iconClass = classNames({
      'field-icon': true,
      'icon-search': !isLoading || lockedState || disabledState,
      loader: isLoading && !(lockedState || disabledState),
    });

    return (
      <AbstractControl
        {...inputAbstractProps}
        containerProps={{ onBlur: this.handleOnFocusout }}
        forwardWrapperRef={this.handleWrapperRefSet}
      >
        <React.Fragment>
          <span className={iconClass} />
          <input
            name={this.inputNameValue}
            autoComplete={this.inputAutocompleteValue}
            type="text"
            id={id}
            className="autocomplete-input"
            ref={this.inputElemRef}
            value={this.state.inputValue}
            disabled={lockedState || disabledState}
            placeholder={placeholderTextStr || ''}
            onChange={this.fieldOnChange}
            onFocus={this.handleOnFocus}
            onKeyDown={this.handleInputKeyDown}
          />

          {isShowResultCnt && (
            <ResultsCount clearFieldHandler={this.clearFieldValue} count={this.state.optionsList.length} />
          )}

          {this.state.optionsList.length > 0 && this.state.isOpen && (
            <AutocompleteOptionsList
              id={id}
              value={value}
              fullWidth={fullWidth}
              isOpen={this.state.isOpen}
              onSelectOption={this.handleSelectOption}
              optionsList={this.state.optionsList}
              optionsListTitle={listTitle}
              optionsListClassName={optionsListClassName}
              optionsCount={this.state.optionsCount}
              renderMoreOptions={this.renderMoreOptions}
            />
          )}
        </React.Fragment>
      </AbstractControl>
    );
  }

  renderMoreOptions() {
    this.setState({
      optionsCount: this.state.optionsCount + this.props.optionsToAdd,
    });
  }

  closeDropDown() {
    if (typeof this.props.onBlur === 'function') {
      this.props.onBlur();
    }

    let additionalState = null;

    if (this.props.clearInvalidOnBlur && this.props.options.length) {
      // Do a strict match first to support similar labels with different letter case
      const currentValue = this.state.inputValue || '';
      let activeOption = this.props.options.find(o => o.text.trim() === currentValue.trim());

      if (!activeOption && currentValue.trim()) {
        activeOption = this.props.options.find(o => o.text.toLowerCase().trim() === currentValue.toLowerCase().trim());
      }

      if (!activeOption) {
        if (currentValue !== '') {
          additionalState = { inputValue: '' };
        }
        this.handleOnChange(null);
      } else {
        if (currentValue !== activeOption.text) {
          additionalState = { inputValue: activeOption.text };
        }
        this.handleOnChange(activeOption.value);
      }
    }

    this.hideDropDown(additionalState);
  }

  hideDropDown(additionalState = null) {
    const nextState = {
      optionsList: this.props.options,
      isType: false,
      isOpen: false,
    };

    if (additionalState) {
      Object.assign(nextState, additionalState);
    }

    this.setState(nextState);
  }

  handleOnFocusout(ev) {
    if (!ev.relatedTarget) {
      this.closeDropDown();

      return;
    }

    const containerNode = ev.relatedTarget.closest('.autocomplete-input-component');
    const focusMovedToRelatedNode = containerNode !== null;

    if (!focusMovedToRelatedNode) {
      this.closeDropDown();
    }
  }

  openDropDown() {
    const currentValue = this.state.inputValue || '';

    if (currentValue.length > 0) {
      const optionsList = this.props.options.filter(o => {
        return o.text.toLowerCase().trim().includes(currentValue.toLowerCase().trim());
      });
      this.setState({ optionsList, isType: true, isOpen: true });
    } else {
      this.setState({ optionsList: this.props.options, isOpen: true });
    }
  }

  handleOnFocus() {
    this.openDropDown();
  }

  clearFieldValue() {
    this.setState({ inputValue: '', optionsList: this.props.options, isType: true });
  }

  fieldOnChange(event) {
    const nextValue = event?.target?.value || '';
    let optionsList = this.props.options;

    if (nextValue.length) {
      optionsList = this.props.options.filter(o => {
        return o.text.toLowerCase().trim().includes(nextValue.toLowerCase().trim());
      });
    }
    this.setState({ inputValue: nextValue, optionsList, isType: true, isOpen: true });
  }

  handleOnChange(value) {
    if (!this.props.lockedState && !this.props.disabledState && typeof this.props.onChange === 'function') {
      this.props.onChange(value);
    }
  }

  handleSelectOption(event) {
    const { type, value, text } = event.target.dataset;
    const normalizedValue = type === 'number' ? Number(value) : value;

    this.handleOnChange(normalizedValue);
    this.hideDropDown({ inputValue: text || '', isType: false });
  }

  handleInputKeyDown(event) {
    const key = event.key;
    const listElem = this.inputWrapper ? this.inputWrapper.querySelector('.autocomplete-option-list') : null;

    switch (key) {
      case 'ArrowDown':
        if (listElem?.firstChild) {
          listElem.firstChild.focus();
        }
        event.preventDefault();
        break;
      case 'ArrowUp':
        if (listElem?.lastChild) {
          listElem.lastChild.focus();
        }
        event.preventDefault();
        break;
      case 'Escape':
        document.activeElement.blur();
        event.preventDefault();
        break;
      case 'Enter':
        if (this.state.optionsList.length === 1) {
          const option = this.state.optionsList[0];
          if (option.text.toLowerCase().trim() === this.state.inputValue.toLowerCase().trim()) {
            this.setState({ inputValue: option.text }, () => this.inputElemRef.current?.blur());
          }
        }
        event.preventDefault();
        break;
      default:
        break;
    }
  }

  handleWrapperRefSet(ref) {
    this.abstractComponentRef = ref;
    this.updateInputWrapper();
  }
}

export default injectIntl(AutocompleteControl);
